import { useEffect, useState } from "react";

import {
  type ApiDeps,
  type InvoiceTarget,
  fetchApprovals,
  fetchExceptions,
} from "../lib/api";
import { t, tn, type Language, type StringKey } from "../lib/i18n";
import { useRefresh } from "../lib/refresh";
import { formatRelative } from "../lib/relativeTime";
import { useNow } from "../lib/useNow";
import { Icon } from "./icons";

interface NeedsYouProps {
  deps: ApiDeps;
  language: Language;
  /** Shared refresh signal — bump to refetch. Defaults to 0 so the
   * component renders standalone (unit tests) without a RefreshProvider. */
  reloadKey?: number;
  /** Navigate to the Invoices tab, drilled into the row's invoice. */
  onOpen: (target: InvoiceTarget) => void;
}

/** A single triage row, normalised from either an approval or an exception. */
interface NeedsItem {
  /** Local ack key — correlation id + kind (a correlation can have both). */
  id: string;
  kind: "approval" | "review";
  correlationId: string;
  title: string;
  detail: string | null;
  /** Vendor the row is about, when the projection knows it — the row must
   * say WHICH invoice, not only which exception kind, so the CTA isn't the
   * only way to find out (design review mockup 2D-5). */
  vendor: string | null;
  /** ISO-8601 occurrence time → drives the amber "waiting {ago}" age line. */
  occurredAt: string;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; items: NeedsItem[] }
  | { kind: "error" };

/** Friendly per-kind label for a review (exception) row, falling back to a
 * generic "Exception" when the backend grows a kind the SPA has no string
 * for yet. This is now the ONLY exception-rendering surface: the old
 * `ExceptionsFeed` it used to mirror was replaced by this card in the §12
 * redesign and had been shipping unimported ever since (design review
 * finding 10) — it is deleted, so there is nothing left to drift against. */
function reviewTitle(kind: string, language: Language): string {
  const key = `exception.kind.${kind}` as StringKey;
  try {
    return t(key, language);
  } catch {
    return t("exception.kind.unknown", language);
  }
}

/** "Precisa de si" — the single triage surface, promoted to the top of the
 * Overview (§12.6). Composes the existing scoped reads client-side (pending
 * approvals + exceptions, low-confidence items being an `ExceptionKind`) into
 * one action queue — no new backend route, no new truth source.
 *
 * Two visual identities: an attention-amber queue when something is waiting,
 * and a success-green "Tudo tratado" state when nothing is (the common
 * reality at ~6 invoices/mo) — designed explicitly, never a wall of zeros.
 * Universal — no tenant copy. */
export function NeedsYou({
  deps,
  language,
  reloadKey = 0,
  onOpen,
}: NeedsYouProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const { lastRefreshedAt } = useRefresh();
  const now = useNow(30_000);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    Promise.all([
      fetchApprovals(deps, "pending"),
      fetchExceptions(deps),
    ])
      .then(([approvals, exceptions]) => {
        if (cancelled) return;
        const items: NeedsItem[] = [
          ...approvals.map((a) => ({
            id: `${a.correlation_id}:approval`,
            kind: "approval" as const,
            correlationId: a.correlation_id,
            title: a.subject ?? t("needs.approval.title", language),
            detail: null,
            vendor: null,
            occurredAt: a.occurred_at,
          })),
          ...exceptions.map((e) => ({
            id: `${e.correlation_id}:review:${e.event_id}`,
            kind: "review" as const,
            correlationId: e.correlation_id,
            title: reviewTitle(e.kind, language),
            detail: e.detail,
            vendor: e.vendor_slug,
            occurredAt: e.occurred_at,
          })),
        ];
        // Oldest first — the longest-waiting item sits at the top of the queue.
        items.sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));
        setState({ kind: "loaded", items });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
    // language only affects labels of freshly-fetched rows; refetch is keyed
    // on deps + reloadKey (the shared refresh contract).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deps.backendUrl, deps.getAuthToken, reloadKey, language]);

  const lastCheckedAgo =
    lastRefreshedAt === null
      ? t("status.updated_never", language)
      : formatRelative(lastRefreshedAt, now, language);

  if (state.kind === "loading") {
    return (
      <section
        aria-label={t("needs.title", language)}
        aria-busy="true"
        data-testid="needs-loading"
        className="h-28 animate-pulse rounded-[2px] border border-kwanza-divider bg-kwanza-card shadow-sm"
      />
    );
  }

  // A degraded read must not masquerade as "all handled" — surface a quiet,
  // non-blocking note (the rest of Overview still renders).
  if (state.kind === "error") {
    return (
      <section
        aria-label={t("needs.title", language)}
        data-testid="needs-error"
        className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 text-sm text-kwanza-inkSoft shadow-sm"
      >
        {t("error.fetch_failed.body", language)}
      </section>
    );
  }

  const visible = state.items;

  // Healthy / empty state — flip the whole card to success-green. A top
  // rule rather than a left stripe: `border-l-4` accent bars aren't in the
  // DS vocabulary, which uses flat bordered cards with a top rule (design
  // review finding 10).
  if (visible.length === 0) {
    return (
      <section
        aria-label={t("needs.title", language)}
        data-testid="needs-ok"
        className="flex items-center gap-3 rounded-[2px] border border-kwanza-successFill border-t-[3px] border-t-kwanza-successText bg-kwanza-successTint p-4 shadow-sm"
      >
        <span className="relative inline-block shrink-0">
          <img
            src="/agent-avatar.webp"
            alt=""
            aria-hidden="true"
            width={44}
            height={44}
            className="h-11 w-11 rounded-full bg-kwanza-card object-cover"
          />
          <span
            className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-[2px] bg-kwanza-successText ring-2 ring-kwanza-card"
            aria-hidden="true"
          >
            <Icon name="check-circle" size={11} className="text-kwanza-card" />
          </span>
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-base font-bold text-kwanza-ink">
            {t("needs.ok.title", language)}
          </div>
          <div className="text-sm text-kwanza-inkSoft">
            {tn("needs.ok.body", { ago: lastCheckedAgo }, language)}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label={t("needs.title", language)}
      data-testid="needs-you"
      className="overflow-hidden rounded-[2px] border border-kwanza-warnFill border-t-[3px] border-t-kwanza-warnRule bg-kwanza-card shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 bg-kwanza-warnBanner px-4 py-3">
        <div className="flex items-center gap-2">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-[2px] bg-kwanza-warnFill"
            aria-hidden="true"
          >
            <Icon name="alert-triangle" size={16} className="text-kwanza-ink" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-base font-bold text-kwanza-ink">
              {t("needs.title", language)}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-kwanza-warnText">
              {t("needs.eyebrow", language)}
            </span>
          </span>
        </div>
        <span
          className="shrink-0 rounded-full bg-kwanza-warnFill px-2.5 py-0.5 text-xs font-bold text-kwanza-ink"
          data-testid="needs-count"
        >
          {tn(
            visible.length === 1 ? "needs.count.one" : "needs.count.other",
            { n: visible.length },
            language,
          )}
        </span>
      </div>

      <ul className="divide-y divide-kwanza-divider">
        {visible.map((it) => (
          <li
            key={it.id}
            data-testid="needs-row"
            className="flex flex-wrap items-start gap-3 p-4"
          >
            <span
              className={
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-[2px] " +
                (it.kind === "approval"
                  ? "bg-kwanza-infoTint text-kwanza-info"
                  : "bg-kwanza-warnTint text-kwanza-warnText")
              }
              aria-hidden="true"
            >
              <Icon name={it.kind === "approval" ? "info" : "file-text"} size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-kwanza-ink">{it.title}</div>
              {/* Lead with WHICH invoice, not only which exception kind
               * (mockup 2D-5) — otherwise the CTA is the only way to find
               * out what the row is about. */}
              {it.vendor || it.detail ? (
                <div className="text-xs text-kwanza-inkSoft" data-testid="needs-subject">
                  {[it.vendor, it.detail].filter(Boolean).join(" · ")}
                </div>
              ) : null}
              <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-kwanza-warnText">
                <Icon name="clock" size={12} />
                {tn(
                  "needs.age.waiting",
                  { ago: formatRelative(Date.parse(it.occurredAt), now, language) },
                  language,
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onOpen({ correlationId: it.correlationId })}
              data-testid="needs-cta"
              className="inline-flex shrink-0 items-center gap-1 rounded-md bg-kwanza-accent px-3 py-1.5 text-sm font-bold text-kwanza-onAccent hover:bg-kwanza-accentHover"
            >
              {t(
                it.kind === "approval" ? "needs.cta.approval" : "needs.cta.review",
                language,
              )}
              <Icon name="arrow-right" size={14} />
            </button>
          </li>
        ))}
      </ul>

      {/* Scope + where the real decision happens (design review findings 4
       * and 7). The card is unwindowed — it deliberately ignores the period
       * picker above it — and it is read-only: the shipped "Marcar como
       * tratado" hid the row in local state while the approval stayed
       * pending server-side, so the row came back on the next refresh. On
       * the one card the product's credibility rests on, that reads as a
       * decision that didn't stick. */}
      <p
        className="border-t border-kwanza-divider bg-kwanza-surface px-4 py-2.5 text-xs leading-relaxed text-kwanza-inkSoft"
        data-testid="needs-scope-note"
      >
        {t("needs.scope_note", language)}
      </p>
    </section>
  );
}
